import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
} from "@angular/core";
import {Subscription} from "rxjs";
import {NotificationType} from "src/app/enums/Notification.enum";
import {
  HandleWorkCompanionActionResult,
  Notification,
  WorkNotification,
} from "src/app/interfaces/notification.interface";
import {User} from "src/app/modules/auth/models/User";
import {ToastService} from "src/app/modules/core/services/toast.service";
import {Friend} from "src/app/modules/friends/interfaces/friend.interface";
import {FriendsService} from "src/app/modules/friends/services/friends.service";
import {CustomError} from "src/app/modules/shared/interfaces/CustomError.interface";
import {formatDistanceToNow} from "date-fns";
import {fr, enUS} from "date-fns/locale";
import {isEqual, omit} from "lodash-es";
import {ListsService} from "src/app/modules/shared/services/list.service";
import {TranslateModule} from "@ngx-translate/core";
import {WorksModel} from "src/app/models/WorksModel";
import {isBook, isComic, isManga} from "src/app/utils/works-type-guards.util";
import {UserService} from "src/app/modules/shared/services/user.service";
import {AuthService} from "src/app/modules/auth/services/auth.service";
import {NgClass} from "@angular/common";
import {RouterLink} from "@angular/router";
import {WorksService} from "src/app/modules/shared/services/works.service";
import {NotificationsService} from "src/app/services/notifications.service";
import {UserAvatarComponent} from "src/app/modules/shared/components/user-avatar/user-avatar.component";
import {WorkQuickRatingComponent} from "src/app/modules/shared/components/work-quick-rating/work-quick-rating.component";
import {ModalService} from "src/app/modules/core/services/modal.service";
import {RecommendationDiscussionsService} from "src/app/modules/discussions/services/recommendation-discussions.service";

@Component({
  selector: "app-notification",
  templateUrl: "./notification.component.html",
  styleUrls: ["./notification.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    RouterLink,
    TranslateModule,
    UserAvatarComponent,
    WorkQuickRatingComponent,
  ],
})
export class NotificationComponent implements OnInit, OnChanges, OnDestroy {
  private readonly discussions = inject(RecommendationDiscussionsService);
  @Input() user!: User;
  @Input() notification!: Notification;
  @Input() isOpened = false;
  @Input() isMobile = false;
  public notificationsTypes = NotificationType;
  public message = "";
  public comment = "";
  public friendRequestAnswered = false;
  public listInvitationAnswered = false;
  public workCompanionAnswered = false;
  public workCompanionPromptVisible = false;
  public ratingPromptDismissed = false;
  public workCompanionRatingPromptContext?: "completed" | "advanced";
  public imagesPath?: string;
  public isBook = isBook;
  public isManga = isManga;
  public isComic = isComic;
  private readonly subscriptions: Subscription[] = [];

  constructor(
    public friendsService: FriendsService,
    public worksService: WorksService,
    private readonly modalService: ModalService,
    private readonly authService: AuthService,
    private readonly userService: UserService,
    private readonly listsService: ListsService,
    private readonly notificationsService: NotificationsService,
    private readonly toastService: ToastService,
    private readonly cdr: ChangeDetectorRef
  ) {
    // Do nothing
  }

  ngOnInit() {
    const type = this.notification.type;
    const message = this.notification.message;

    const typeToMessage: Record<string, string> = {
      [this.notificationsTypes.RecommendationMessage]: "RECOMMENDATION_MESSAGE",
      [this.notificationsTypes.SteamGameAdded]:
        "TIME_TRACKING.STEAM_GAME_ADDED",
      [this.notificationsTypes.LocalFriendJoined]: "LOCAL_FRIEND_JOINED",
      [this.notificationsTypes.WorkAddedToCollaborativeCollection]:
        "WORK_ADDED_COLLABORATIVE_COLLECTION",
      [this.notificationsTypes.ListRemoval]: "REMOVED_FROM_LIST",
      [this.notificationsTypes.ListLeft]: "GONE_FROM_LIST",
      [this.notificationsTypes.RecommendationAdded]: "RECOMMENDATION_ADDED",
      [this.notificationsTypes.WorkCompanionTag]: "WORK_COMPANION_TAG",
      [this.notificationsTypes.WorkTagAccepted]: "WORK_COMPANION_ACCEPTED",
      [this.notificationsTypes.WorkTagDeclined]: "WORK_COMPANION_DECLINED",
      [this.notificationsTypes.WorkRatingPrompt]: "WORK_RATING_PROMPT_SYNC",
    };

    if (type === this.notificationsTypes.FriendRequest) {
      const friendRequestMap: Record<string, string> = {
        FRIEND_REQUEST_ACCEPTED: "FRIEND_REQUEST_ACCEPTED",
        FRIEND_REQUEST_DECLINED: "FRIEND_REQUEST_DECLINED",
      };
      this.message = friendRequestMap[message] ?? "SENT_FRIEND_REQUEST";
    } else if (type === this.notificationsTypes.Recommendation) {
      this.message =
        (this.notification.workCount ?? 1) > 1
          ? "NEW_WORK_RECOMMENDATIONS"
          : "NEW_WORK_RECOMMENDATION";
      if (message !== "NEW_WORK_RECOMMENDATION") {
        this.comment = message;
      }
    } else if (type === this.notificationsTypes.RecommendationAdded) {
      this.message = "WORK_RECOMMENDATION_ADDED";
    } else if (type === this.notificationsTypes.ListInvitation) {
      const listInvitationMap: Record<string, string> = {
        LIST_INVITATION_ACCEPTED: "LIST_INVITATION_ACCEPTED",
        LIST_INVITATION_DECLINED: "LIST_INVITATION_DECLINED",
      };
      this.message = listInvitationMap[message] ?? "SENT_LIST_INVITATION";
    } else if (typeToMessage[type]) {
      this.message = typeToMessage[type];
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes["isOpened"]?.previousValue === true &&
      changes["isOpened"]?.currentValue === false
    ) {
      this.workCompanionPromptVisible = false;
      this.ratingPromptDismissed = false;
      this.workCompanionRatingPromptContext = undefined;
    }

    if (changes?.["user"]) {
      const userCompared = omit(changes["user"]["currentValue"], [
        "tokens",
        "updatedAt",
      ]);
      const userToCompare = omit(changes["user"]["previousValue"], [
        "tokens",
        "updatedAt",
      ]);

      if (
        this.notification.friendRequestId &&
        !isEqual(userCompared, userToCompare)
      ) {
        const friendRequestId = this.getFriendRequestId();
        if (!friendRequestId) {
          return;
        }

        const friendRequestStatusSubscription = this.friendsService
          .getFriendsRequestById(this.user._id, friendRequestId)
          .subscribe({
            next: (res: Friend | null) => {
              if (!res) {
                this.friendRequestAnswered = true;
              }
              this.cdr.markForCheck();
            },
          });
        this.subscriptions.push(friendRequestStatusSubscription);
      }
    }
  }

  public getRoute(type: NotificationType): string {
    switch (type) {
      case this.notificationsTypes.ReleaseDigest:
        return "/latest-releases";
      case this.notificationsTypes.FriendRequest:
        if (
          this.friendRequestAnswered ||
          (this.notification.sender &&
            this.friendsService.isAlreadyFriend(
              this.notification.sender,
              this.user
            ))
        ) {
          return "/friends";
        } else {
          return "/friends/requests";
        }
      case this.notificationsTypes.LocalFriendJoined:
        return "/friends";
      case this.notificationsTypes.ListInvitation:
        if (
          this.listInvitationAnswered ||
          this.hasAlreadyJoinedList() ||
          this.hasAlreadyJoinedList(true)
        ) {
          return "/collections/" + this.notification.list?.slug;
        } else {
          return "/collections/invitations";
        }
      case this.notificationsTypes.ListLeft:
        return "/collections/" + this.notification.list?.slug;
      case this.notificationsTypes.ListRemoval:
        return "/collections";
      case this.notificationsTypes.WorkAddedToCollaborativeCollection:
        return "/collections/" + this.notification.list?.slug;
      case this.notificationsTypes.Recommendation:
        return "/recommendations";
      case this.notificationsTypes.RecommendationAdded:
        return "/users/" + this.notification.sender?.username;
      case this.notificationsTypes.WorkCompanionTag:
      case this.notificationsTypes.WorkTagAccepted:
      case this.notificationsTypes.WorkTagDeclined:
      case this.notificationsTypes.WorkRatingPrompt:
        return "/notifications";
      default:
        return "";
    }
  }

  public getRelativeTime(date: string | Date): string {
    return formatDistanceToNow(new Date(date), {
      addSuffix: true,
      locale: this.user.language === "fr" ? fr : enUS,
    });
  }

  public hasAlreadyJoinedList(friend = false): boolean {
    return (
      Array.isArray(this.user.lists) &&
      this.user.lists.some(
        (list) =>
          list._id === this.notification.list?._id &&
          list.collaborators?.some(
            (collaborator) =>
              collaborator.user?._id === this.notification.user ||
              (friend &&
                collaborator.user?._id === this.notification.sender?._id)
          )
      )
    );
  }

  public acceptListInvitation(listInvitationId: string) {
    const acceptFriendSubscription = this.listsService
      .acceptListInvitation(listInvitationId)
      .subscribe({
        next: (res: boolean | null) => {
          if (res) {
            const userSubscription = this.userService
              .getUser(this.user._id)
              .subscribe({
                next: (user: User | null) => {
                  if (user) {
                    this.authService.user = user;
                    this.user = user;
                    this.cdr.markForCheck();
                  }
                },
              });
            this.subscriptions.push(userSubscription);

            this.toastService.sendToast({
              text: `YOU_ACCEPTED_LIST_INVITATION`,
              classes: "bg-form-success",
            });
            this.listInvitationAnswered = true;
            this.updateNotificationInStore({
              read: true,
              listInvitationId: this.buildListInvitationStatusRef(
                this.notification.listInvitationId,
                "accepted"
              ),
            });
            this.cdr.markForCheck();
          }
        },
        error: (error: CustomError) => {
          this.toastService.sendToast({
            text: error.error.error,
            classes: "bg-form-error",
          });
        },
      });
    this.subscriptions.push(acceptFriendSubscription);
  }

  public rejectListInvitation(listInvitationId: string) {
    const acceptFriendSubscription = this.listsService
      .rejectListInvitation(listInvitationId)
      .subscribe({
        next: (res: boolean | null) => {
          if (res) {
            this.toastService.sendToast({
              text: `YOU_DECLINED_LIST_INVITATION`,
              classes: "bg-form-success",
            });
            this.listInvitationAnswered = true;
            this.updateNotificationInStore({
              read: true,
              listInvitationId: this.buildListInvitationStatusRef(
                this.notification.listInvitationId,
                "rejected"
              ),
            });
            this.cdr.markForCheck();
          }
        },
        error: (error: CustomError) => {
          this.toastService.sendToast({
            text: error.error.error,
            classes: "bg-form-error",
          });
        },
      });
    this.subscriptions.push(acceptFriendSubscription);
  }

  public acceptFriendRequest(friendRequestId: string) {
    this.friendsService.handleFriendAction(
      this.friendsService.acceptFriendRequest(friendRequestId),
      "YOU_ACCEPTED_FRIEND_REQUEST",
      () => {
        this.friendRequestAnswered = true;
        this.updateNotificationInStore({
          read: true,
          friendRequestId: this.buildStatusRef(
            this.notification.friendRequestId,
            "accepted"
          ),
        });
        this.cdr.markForCheck();
      }
    );
  }

  public rejectFriendRequest(friendRequestId: string) {
    this.friendsService.handleFriendAction(
      this.friendsService.rejectFriendRequest(friendRequestId),
      "YOU_DECLINED_FRIEND_REQUEST",
      () => {
        this.friendRequestAnswered = true;
        this.updateNotificationInStore({
          read: true,
          friendRequestId: this.buildStatusRef(
            this.notification.friendRequestId,
            "declined"
          ),
        });
        this.cdr.markForCheck();
      }
    );
  }

  public getFriendRequestId(): string | null {
    const {friendRequestId} = this.notification;

    if (typeof friendRequestId === "string") {
      return friendRequestId;
    }

    return friendRequestId?._id ?? null;
  }

  public handleWorkCompanionAction(
    action: "accept" | "accept_with_sync" | "decline"
  ): void {
    if (!this.notification._id) {
      return;
    }

    this.notificationsService
      .handleWorkCompanionAction(this.notification._id, this.user._id, action)
      .subscribe({
        next: (result: HandleWorkCompanionActionResult) => {
          this.workCompanionAnswered = true;
          this.workCompanionPromptVisible =
            action !== "decline" && result.shouldPromptRating;
          this.workCompanionRatingPromptContext =
            result.ratingPromptContext ?? undefined;
          this.updateNotificationInStore({
            workCompanionId: this.buildStatusRef(
              this.notification.workCompanionId,
              result.companion.status,
              {
                syncEnabled: result.companion.syncEnabled,
                syncConsent: result.companion.syncConsent,
              }
            ),
          });
          this.cdr.markForCheck();
        },
        error: (error: CustomError) => {
          this.toastService.sendToast({
            text: error.error.error,
            classes: "bg-form-error",
          });
        },
      });
  }

  public getNotificationWorkTitle(work: WorkNotification): string {
    if (this.isBook(work) || this.isComic(work) || this.isManga(work)) {
      return work.title;
    }

    return this.worksService.getItemTitle(work);
  }

  public getNotificationWorkCover(work: WorkNotification): string {
    if (this.isBook(work) || this.isComic(work) || this.isManga(work)) {
      return `${this.worksService.imagesDomain}/${
        work.type
      }${this.worksService.addSizeToFileName(work.cover, "")}`;
    }

    return this.worksService.getItemCover(work, "", false);
  }

  public hasNotificationWorkCover(work: WorkNotification): boolean {
    if (this.isBook(work) || this.isComic(work) || this.isManga(work)) {
      return !!this.worksService.hasItemCover(work);
    }

    return !!(this.worksService.getTranslatedCover(work) ?? work.cover);
  }

  public shouldOpenWorkModal(): boolean {
    return (
      !!this.notification.work &&
      (this.notification.type === NotificationType.Recommendation ||
        this.notification.type === NotificationType.RecommendationMessage ||
        this.notification.type === NotificationType.SteamGameAdded ||
        this.notification.type === NotificationType.WorkCompanionTag)
    );
  }

  public shouldShowRatingPrompt(): boolean {
    return (
      !this.ratingPromptDismissed &&
      !!this.notification.work &&
      (this.workCompanionPromptVisible ||
        this.notification.type === NotificationType.WorkRatingPrompt)
    );
  }

  public getRatingPromptContext(): "completed" | "advanced" | undefined {
    if (this.workCompanionPromptVisible) {
      return this.workCompanionRatingPromptContext;
    }

    if (this.notification.type !== NotificationType.WorkRatingPrompt) {
      return undefined;
    }

    return this.notification.message === "WORK_RATING_PROMPT_ADVANCED_SYNC"
      ? "advanced"
      : "completed";
  }

  public handleNotificationClick(): void {
    if (this.notification.recommendationId) {
      this.closeNotificationsModal();
      this.discussions.open(this.notification.recommendationId);
      return;
    }
    if (!this.shouldOpenWorkModal() || !this.notification.work) {
      this.closeNotificationsModal();
      return;
    }

    const detailsComponent = this.worksService.getWorksDetailsComponentByType(
      this.notification.work.type
    );
    if (!detailsComponent) {
      return;
    }

    this.modalService.openWorkModal(
      this.notification.work as unknown as WorksModel,
      detailsComponent,
      false
    );

    if (this.notification.type === NotificationType.Recommendation) {
      this.markNotificationAsRead();
    }
  }

  public onNotificationKeyUp(event: KeyboardEvent): void {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    (event.currentTarget as HTMLElement | null)?.click();
  }

  public isEffectivelyUnread(notification: Notification): boolean {
    return this.notificationsService.isEffectivelyUnread(notification);
  }

  public requiresAction(notification: Notification): boolean {
    return this.notificationsService.requiresAction(notification);
  }

  private updateNotificationInStore(patch: Partial<Notification>): void {
    if (!this.notification._id) {
      return;
    }

    this.notificationsService.updateNotifications((currentNotifications) =>
      currentNotifications.map((notification) => {
        if (notification._id !== this.notification._id) {
          return notification;
        }

        return {
          ...notification,
          ...patch,
        };
      })
    );

    this.notification =
      this.notificationsService.notificationsSubject.value.find(
        (notification) => notification._id === this.notification._id
      ) ?? this.notification;
  }

  private markNotificationAsRead(): void {
    if (!this.notification._id || this.notification.read) {
      return;
    }

    const markAsReadSubscription = this.notificationsService
      .markNotificationsAsRead(this.user._id, this.notification._id)
      .subscribe({
        next: (result: boolean) => {
          if (!result) {
            return;
          }

          this.updateNotificationInStore({read: true});
          this.cdr.markForCheck();
        },
      });

    this.subscriptions.push(markAsReadSubscription);
  }

  private closeNotificationsModal(): void {
    if (!this.isMobile) {
      return;
    }

    this.modalService.closeModal("notifications");
  }

  private buildStatusRef(
    reference:
      | string
      | {
          _id?: string;
          status?: string;
          syncEnabled?: boolean;
          syncConsent?: boolean;
        }
      | undefined,
    status: string,
    extra: {syncEnabled?: boolean; syncConsent?: boolean} = {}
  ): {
    _id?: string;
    status: string;
    syncEnabled?: boolean;
    syncConsent?: boolean;
  } {
    if (typeof reference === "string") {
      return {
        _id: reference,
        status,
        ...extra,
      };
    }

    return {
      ...reference,
      status,
      ...extra,
    };
  }

  private buildListInvitationStatusRef(
    reference: Notification["listInvitationId"],
    status: string
  ): Notification["listInvitationId"] {
    if (!reference) {
      return reference;
    }

    return {
      ...reference,
      status,
    };
  }

  ngOnDestroy() {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }
}
